import type {
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
  CalendarRouteTransition,
} from "../../../../../api/kehrblatt/schedule-events"
import { internalOrderTypeLabel } from "../../../../../_shared/_logic/order-type-labels"

const GREEN_MAX_DISTANCE_KM = 3
const YELLOW_MAX_DISTANCE_KM = 8
const ASSUMED_SPEED_KMH = 40
const OPERATIONALLY_ZERO_TRAVEL_MINUTES = 3
const TERMINAL_STATUSES = new Set(["CANCELED", "NOSHOW"])

type BuildCalendarRouteTransitionsInput = {
  events: ScheduleEvent[]
  workOrderById: Record<string, CalendarBoardWorkOrderRef>
  propertyById: Record<string, CalendarBoardPropertyRef>
}

type RouteEvent = {
  event: ScheduleEvent
  workOrder?: CalendarBoardWorkOrderRef
  property?: CalendarBoardPropertyRef
  anchorKind: "EVENT" | "FLEX_WINDOW"
  start: Date
  end: Date
  employeeId: string
  dayKey: string
}

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null

  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const localDayKey = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const normalizeCoordinate = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null

const distanceKmBetween = (
  previous?: CalendarBoardPropertyRef,
  next?: CalendarBoardPropertyRef,
): number | null => {
  const previousLat = normalizeCoordinate(previous?.geo_lat)
  const previousLng = normalizeCoordinate(previous?.geo_lng)
  const nextLat = normalizeCoordinate(next?.geo_lat)
  const nextLng = normalizeCoordinate(next?.geo_lng)

  if (previousLat === null || previousLng === null || nextLat === null || nextLng === null)
    return null

  const earthRadiusKm = 6371
  const toRad = (value: number) => (value * Math.PI) / 180
  const latDistance = toRad(nextLat - previousLat)
  const lngDistance = toRad(nextLng - previousLng)
  const a =
    Math.sin(latDistance / 2) * Math.sin(latDistance / 2) +
    Math.cos(toRad(previousLat)) *
      Math.cos(toRad(nextLat)) *
      Math.sin(lngDistance / 2) *
      Math.sin(lngDistance / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return Math.round(earthRadiusKm * c * 10) / 10
}

const routeGradeForDistance = (distanceKm: number): "GREEN" | "YELLOW" | "RED" => {
  if (distanceKm <= GREEN_MAX_DISTANCE_KM) return "GREEN"

  if (distanceKm <= YELLOW_MAX_DISTANCE_KM) return "YELLOW"

  return "RED"
}

const reasonForDistance = (distanceKm: number): string => {
  if (distanceKm <= GREEN_MAX_DISTANCE_KM) return "near_day_route"

  if (distanceKm <= YELLOW_MAX_DISTANCE_KM) return "acceptable_day_route"

  return "far_from_day_route"
}

const estimatedTravelMinutes = (distanceKm: number): number =>
  Math.max(0, (distanceKm / Math.max(ASSUMED_SPEED_KMH, 1)) * 60)

const roundedTravelMinutes = (estimatedMinutes: number | null): number => {
  if (estimatedMinutes === null) return 10

  if (estimatedMinutes < OPERATIONALLY_ZERO_TRAVEL_MINUTES) return 0

  return Math.ceil(estimatedMinutes / 10) * 10
}

const enumValue = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toUpperCase()

const labelForEvent = (item: RouteEvent): string | null => {
  const title = `${item.event.title ?? ""}`.trim()

  if (title) return title

  return internalOrderTypeLabel(item.workOrder)
}

const isTransitionAnchor = (event: ScheduleEvent): boolean => {
  if (TERMINAL_STATUSES.has(enumValue(event.status))) return false

  const eventType = enumValue(event.event_type)

  if (eventType === "APPOINTMENT") return true

  if (["ABSENCE", "INTERNAL_BLOCK"].includes(eventType)) {
    return !Boolean(event.all_day)
  }

  return false
}

const asRouteEvent = (
  event: ScheduleEvent,
  workOrderById: Record<string, CalendarBoardWorkOrderRef>,
  propertyById: Record<string, CalendarBoardPropertyRef>,
): RouteEvent | null => {
  if (!isTransitionAnchor(event)) return null

  const employeeId = String(event.employee_id ?? "").trim()

  if (!employeeId || !event.id) return null

  const isSoft = event.event_type === "APPOINTMENT" && enumValue(event.flex_type) === "SOFT"
  const start =
    parseDate(isSoft ? event.timeframe_start_at : event.start_at) ?? parseDate(event.start_at)
  const end = parseDate(isSoft ? event.timeframe_end_at : event.end_at) ?? parseDate(event.end_at)

  if (!start || !end || end <= start) return null

  const workOrder = event.work_order_id ? workOrderById[event.work_order_id] : undefined
  const propertyId = event.property_id ?? workOrder?.property_id ?? null
  const property = propertyId ? propertyById[propertyId] : undefined

  return {
    event,
    workOrder,
    property,
    anchorKind: isSoft ? "FLEX_WINDOW" : "EVENT",
    start,
    end,
    employeeId,
    dayKey: localDayKey(start),
  }
}

const relationKind = (
  previous: RouteEvent,
  next: RouteEvent,
): CalendarRouteTransition["relation_kind"] => {
  if (previous.anchorKind === "FLEX_WINDOW" && next.anchorKind === "FLEX_WINDOW") {
    return "FLEX_WINDOW_TO_FLEX_WINDOW"
  }

  if (previous.anchorKind === "FLEX_WINDOW") return "FLEX_WINDOW_TO_EVENT"

  if (next.anchorKind === "FLEX_WINDOW") return "EVENT_TO_FLEX_WINDOW"

  return "EVENT_TO_EVENT"
}

const buildTransition = (
  previous: RouteEvent,
  next: RouteEvent,
  sequenceIndex: number,
): CalendarRouteTransition | null => {
  if (previous.employeeId !== next.employeeId || previous.dayKey !== next.dayKey) return null

  const distanceKm = distanceKmBetween(previous.property, next.property)
  const estimatedMinutes = distanceKm === null ? null : estimatedTravelMinutes(distanceKm)
  const roundedMinutes = roundedTravelMinutes(estimatedMinutes)
  const gapMinutes = Math.max(
    0,
    Math.round((next.start.getTime() - previous.end.getTime()) / 60000),
  )
  const overlapMinutes = Math.max(
    0,
    Math.round((previous.end.getTime() - next.start.getTime()) / 60000),
  )

  return {
    id: `calendar-route:${previous.employeeId}:${previous.dayKey}:${previous.event.id}:${next.event.id}`,
    employee_id: previous.employeeId,
    day: previous.dayKey,
    sequence_index: sequenceIndex,
    relation_kind: relationKind(previous, next),
    previous_event_id: previous.event.id,
    next_event_id: next.event.id,
    previous_anchor_kind: previous.anchorKind,
    next_anchor_kind: next.anchorKind,
    previous_anchor_start_at: previous.start.toISOString(),
    previous_anchor_end_at: previous.end.toISOString(),
    next_anchor_start_at: next.start.toISOString(),
    next_anchor_end_at: next.end.toISOString(),
    previous_event_type: previous.event.event_type,
    next_event_type: next.event.event_type,
    previous_event_label: labelForEvent(previous),
    next_event_label: labelForEvent(next),
    previous_work_order_number: previous.workOrder?.order_number ?? null,
    next_work_order_number: next.workOrder?.order_number ?? null,
    gap_start_at: (gapMinutes > 0 ? previous.end : next.start).toISOString(),
    gap_end_at: next.start.toISOString(),
    gap_minutes: gapMinutes,
    overlap_minutes: overlapMinutes,
    distance_km: distanceKm,
    estimated_travel_minutes: estimatedMinutes ?? null,
    rounded_travel_minutes: roundedMinutes,
    route_grade: distanceKm === null ? "UNKNOWN" : routeGradeForDistance(distanceKm),
    missing_coordinates: distanceKm === null,
    reason: distanceKm === null ? "route_transition_unavailable" : reasonForDistance(distanceKm),
  }
}

export const buildCalendarRouteTransitionsFromEvents = ({
  events,
  workOrderById,
  propertyById,
}: BuildCalendarRouteTransitionsInput): CalendarRouteTransition[] => {
  const byEmployeeDay = new Map<string, RouteEvent[]>()

  for (const event of events) {
    const routeEvent = asRouteEvent(event, workOrderById, propertyById)

    if (!routeEvent) continue

    const groupKey = `${routeEvent.employeeId}:${routeEvent.dayKey}`
    const group = byEmployeeDay.get(groupKey) ?? []
    group.push(routeEvent)
    byEmployeeDay.set(groupKey, group)
  }

  const transitions: CalendarRouteTransition[] = []
  for (const group of byEmployeeDay.values()) {
    group.sort((a, b) => {
      const startDelta = a.start.getTime() - b.start.getTime()

      if (startDelta !== 0) return startDelta

      const endDelta = a.end.getTime() - b.end.getTime()

      if (endDelta !== 0) return endDelta

      return a.event.id.localeCompare(b.event.id)
    })
    for (let index = 1; index < group.length; index += 1) {
      const transition = buildTransition(group[index - 1], group[index], index - 1)

      if (transition) transitions.push(transition)
    }
  }

  return transitions
}
